import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import * as Highcharts from "highcharts";
import moment from 'moment-timezone';
import * as Moment from "moment";
import {Subscription} from "rxjs";
import {SharedService} from "../../services/shared.service";
import {EPSEvent} from "../../classes/event";
import {Product} from "../../classes/product";

@Component({
  standalone: false,
  selector: 'app-event-performance-chart',
  templateUrl: './event-performance-chart.component.pug',
  styleUrls: ['./event-performance-chart.component.scss']
})
export class EventPerformanceChartComponent implements OnInit, OnDestroy {

  @Input() events:Array<EPSEvent> = null;
  @Input() totalAvailability = null;
  @Input() comProductObj:Product;
  @Input() performanceData = [];

  tzSubscription: Subscription;

  //One chart handles DROP_BY, RANGE, and DROP_TO. It's a stacked area chart, broken down by registration type.
  chart = null;
  chartNowLine = null;
  chartOptions = {
    chart: {
      borderWidth: 0,
      borderColor: '#d8d8d8',
      spacingBottom: 15,
      spacingTop: 10,
      spacingLeft: 10,
      spacingRight: 10,
      marginTop: 100,
      style: {
        color: '#461e7d',
        fontFamily: 'RoobertENEL-Light, sans-serif'
      },
      zoomType: 'x',
      scrollablePlotArea: {
        minWidth: 600,
        scrollPositionX: 1
      },
    },
    xAxis: {
      type: 'datetime',
      dateTimeLabelFormats: {
        second: '<br/>%H:%M:%S',
        minute: '<br/>%H:%M:%S',
        hour: '<br/>%H:%M:%S',
        day: '%Y<br/>%b-%d',
        week: '%Y<br/>%b-%d',
        month: '%Y-%b',
        year: '%Y'
      },
      minTickInterval: 60000*5,
    },
    yAxis: {
      title: {
        rotation: -90,
        x: -10,
      },
    },
    series: [
      {
        threshold: Infinity,
        type: 'areaspline',
        name: 'Obligation',
        color: '#59bc5f',
        fillOpacity: .5,
        lineWidth: 1,
        marker: {
          symbol: 'circle'
        }
      },
      {
        type: 'area',
        name: 'Load Drop To Reduction (Est.)',
        lineWidth: 3,
        stacking: 'normal',
        connectNulls: false,
        color: '#A28EBE',
        marker: {
          symbol: 'circle'
        }
      },
      {
        type: 'area',
        name: 'Load Reduction (Est.)',
        lineWidth: 3,
        stacking: 'normal',
        connectNulls: false,
        color: '#D8BFEA',
        marker: {
          symbol: 'circle'
        }
      },
      {
        type: 'area',
        name: 'Generator Generation (Est.)',
        lineWidth: 3,
        stacking: 'normal',
        connectNulls: false,
        color: '#CFECFF',
        marker: {
          symbol: 'circle'
        }
      },
      {
        type: 'area',
        name: 'Load Metered Generator Generation (Est.)',
        lineWidth: 3,
        stacking: 'normal',
        connectNulls: false,
        color: '#A8D8FF',
        marker: {
          symbol: 'circle'
        }
      },
      {
        type: 'area',
        name: 'Generator Target Generation (Est.)',
        lineWidth: 3,
        stacking: 'normal',
        connectNulls: false,
        color: '#CAF0F8',
        marker: {
          symbol: 'circle'
        }
      },
      {
        type: 'area',
        name: 'Inverse Generator Target Generation (Est.)',
        lineWidth: 3,
        stacking: 'normal',
        connectNulls: false,
        color: '#90C2F5',
        marker: {
          symbol: 'circle'
        }
      },
      {
        type: 'area',
        name: 'Storage Generation (Est.)',
        lineWidth: 3,
        stacking: 'normal',
        connectNulls: false,
        color: '#ffb59f',
        marker: {
          symbol: 'circle'
        }
      },
      {
        type: 'area',
        name: 'Demand On (Est.)',
        lineWidth: 3,
        stacking: 'normal',
        connectNulls: false,
        color: '#FFF380',
        marker: {
          symbol: 'circle'
        }
      },
      {
        type: 'area',
        name: 'Load Drop To Reduction',
        lineWidth: 3,
        stacking: 'normal',
        connectNulls: false,
        color: '#461E7D',
        marker: {
          symbol: 'circle'
        }
      },
      {
        type: 'area',
        name: 'Load Reduction',
        lineWidth: 3,
        stacking: 'normal',
        connectNulls: false,
        color: '#7B2CBF',
        marker: {
          symbol: 'circle'
        }
      },
      {
        type: 'area',
        name: 'Generator Generation',
        lineWidth: 3,
        stacking: 'normal',
        connectNulls: false,
        color: '#0099FF',
        marker: {
          symbol: 'circle'
        }
      },
      {
        type: 'area',
        name: 'Load Metered Generator Generation',
        lineWidth: 3,
        stacking: 'normal',
        connectNulls: false,
        color: '#005A9C',
        marker: {
          symbol: 'circle'
        }
      },
      {
        type: 'area',
        name: 'Generator Target Generation',
        lineWidth: 3,
        stacking: 'normal',
        connectNulls: false,
        color: '#00B4D8',
        marker: {
          symbol: 'circle'
        }
      },
      {
        type: 'area',
        name: 'Inverse Generator Target Generation',
        lineWidth: 3,
        stacking: 'normal',
        connectNulls: false,
        color: '#023E8A',
        marker: {
          symbol: 'circle'
        }
      },
      {
        type: 'area',
        name: 'Storage Generation',
        lineWidth: 3,
        stacking: 'normal',
        connectNulls: false,
        color: '#FF6C40',
        marker: {
          symbol: 'circle'
        }
      },
      {
        type: 'area',
        name: 'Demand On',
        lineWidth: 3,
        stacking: 'normal',
        connectNulls: false,
        color: '#FFE701',
        marker: {
          symbol: 'circle'
        }
      }
    ],
    tooltip: {
      split: true,
    },
    plotOptions: {
      areaspline: {
        marker: {
          enabled: false
        }
      },
      arearange: {
        marker: {
          enabled: false
        }
      },
      area: {
        marker: {
          enabled: false
        }
      }
    },
    title: {
      text: 'Realtime Event Performance',
      y: 15,
      floating: true,
      align: 'left',
      style: {
        color: '#461e7d',
        fontFamily: 'RoobertENEL-Light, sans-serif'
      }
    },
    credits: {
      enabled: false,
    },
    legend:{
      align: 'right',
      verticalAlign: 'top',
      y: 25,
      x: -30,
    }
  };


  constructor(private sharedService: SharedService) { }

  ngOnInit() {
    //Set moment on the window so highcharts can find it
    (window as any)['moment'] = Moment;

    //Redraw with the new timezone when the user changes the header picker
    this.tzSubscription = this.sharedService.getTimeZonePref().subscribe(() => {
      this.updateChartDataAndStuff();
    });
  }

  ngOnDestroy() {
    this.tzSubscription?.unsubscribe();
  }

  updateChartDataAndStuff(): void {

    //We don't have the product back from COM yet or the chart is still being initialized so we wait
    if(!this.comProductObj || !this.events?.length || !this.chart)
      return;

    this.chart.yAxis[0].setTitle({text: 'Usage (' + this.sharedService.getPrezUOM(this.comProductObj) + ')'}, false);

    let loadDropToUsage = [];
    let estimatedLoadDropToUsage = [];
    let loadUsage = [];
    let estimatedLoadUsage = [];
    let generatorUsage = [];
    let estimatedGeneratorUsage = [];
    let loadMeteredGeneratorUsage = [];
    let estimatedLoadMeteredGeneratorUsage = [];
    let generatorTargetUsage = [];
    let estimatedGeneratorTargetUsage = [];
    let inverseGeneratorTargetUsage = [];
    let estimatedInverseGeneratorTargetUsage = [];
    let storageUsage = [];
    let estimatedStorageUsage = [];
    let demandOnUsage = [];
    let estimatedDemandOnUsage = [];
    let obligationRange = [];

    const expectedCapacityByTime = new Map<number, number>();
    this.performanceData.forEach(pd => {
      const t = moment(pd.interval_dttm_utc).valueOf();
      expectedCapacityByTime.set(t, (expectedCapacityByTime.get(t) ?? 0) + (pd.expected_capacity_value ?? 0));
    });

    //Loop through the performance data and add it to the chart
    const now = moment();
    this.performanceData.forEach(pd => {
      const time = moment(pd.interval_dttm_utc).valueOf();
      const totalExpectedCapacity = expectedCapacityByTime.get(time);

      if (this.comProductObj.target_type === "RANGE") {
        let min_target = totalExpectedCapacity * (this.comProductObj.performance_target_min / 100);
        let max_target = totalExpectedCapacity * (this.comProductObj.performance_target_max / 100);
        obligationRange.push([time, this.sharedService.convertProductUOM(min_target, this.comProductObj), this.sharedService.convertProductUOM(max_target, this.comProductObj)]);
      } else {
        obligationRange.push([time, this.sharedService.convertProductUOM(totalExpectedCapacity, this.comProductObj)]);
      }

      if (pd.net_performance !== null && moment(time).isSameOrBefore(now)) {
        const netPerformanceValue = +this.sharedService.convertProductUOM(pd.net_performance, this.comProductObj,false,2);

        switch(pd.registration_type) {
          case "LOAD_DROP_TO":
            (pd.estimated ? estimatedLoadDropToUsage : loadDropToUsage).push([time, netPerformanceValue]);
            break;
          case "LOAD":
            (pd.estimated ? estimatedLoadUsage : loadUsage).push([time, netPerformanceValue]);
            break;
          case "GENERATOR":
            (pd.estimated ? estimatedGeneratorUsage : generatorUsage).push([time, netPerformanceValue]);
            break;
          case "LOAD_METERED_GENERATOR":
            (pd.estimated ? estimatedLoadMeteredGeneratorUsage : loadMeteredGeneratorUsage).push([time, netPerformanceValue]);
            break;
          case "GENERATOR_TARGET":
            (pd.estimated ? estimatedGeneratorTargetUsage : generatorTargetUsage).push([time, netPerformanceValue]);
            break;
          case "INVERSE_GENERATOR_TARGET":
            (pd.estimated ? estimatedInverseGeneratorTargetUsage : inverseGeneratorTargetUsage).push([time, netPerformanceValue]);
            break;
          case "STORAGE":
            (pd.estimated ? estimatedStorageUsage : storageUsage).push([time, netPerformanceValue]);
            break;
          case "DEMAND_ON":
            (pd.estimated ? estimatedDemandOnUsage : demandOnUsage).push([time, netPerformanceValue]);
            break;
        }
      }
    });

    const seriesData = [
      obligationRange,
      estimatedLoadDropToUsage,
      estimatedLoadUsage,
      estimatedGeneratorUsage,
      estimatedLoadMeteredGeneratorUsage,
      estimatedGeneratorTargetUsage,
      estimatedInverseGeneratorTargetUsage,
      estimatedStorageUsage,
      estimatedDemandOnUsage,
      loadDropToUsage,
      loadUsage,
      generatorUsage,
      loadMeteredGeneratorUsage,
      generatorTargetUsage,
      inverseGeneratorTargetUsage,
      storageUsage,
      demandOnUsage,
    ];

    const obligationType = this.comProductObj.target_type === "RANGE" ? 'arearange' : 'areaspline';
    if (this.chart.series[0].type !== obligationType) {
      this.chart.series[0].update({ type: obligationType } as any, false);
    }

    seriesData.forEach((data, index) => {
      this.chart.series[index].update({
        data,
        showInLegend: data.length > 0
      } as any, false);
    });

    this.chart.update({
      time: {
        timezone: this.sharedService.getTimeZoneName(this.events[0].full_time_zone || this.comProductObj.timezone, false)
      }
    }, false);

    //Kind of weird it has to be done in two steps, but it does, otherwise we get a datestring instead of a number
    let nowUTC = moment().toISOString();
    this.chartNowLine = moment(nowUTC).valueOf();

    this.chart.xAxis[0].removePlotLine("start-line", false);
    this.chart.xAxis[0].addPlotLine({
      id: "start-line",
      value: moment(this.events[0].event_start_dttm_utc).valueOf(),
      color: 'black',
      dashStyle: 'longdashdot',
      width: 2,
      label: {
        text: "Event Start",
        rotation: 0,
        x: -65
      }
    }, false);

    this.chart.xAxis[0].removePlotLine("now-line", false);
    this.chart.xAxis[0].addPlotLine({
      id: "now-line",
      value: this.chartNowLine,
      color: 'green',
      width: 2,
      label: {
        text: "NOW",
        rotation: 0,
        y: -15
      }
    }, false);

    this.chart.xAxis[0].removePlotLine("end-line", false);
    this.chart.xAxis[0].addPlotLine({
      id: "end-line",
      value: moment(this.events[0].event_end_dttm_utc).valueOf(),
      color: 'black',
      dashStyle: 'longdashdot',
      width: 2,
      label: {
        text: "Event End",
        rotation: 0
      }
    }, false);

    this.chart.redraw(false);
  }

  setChart(chart: Highcharts.Chart): void {
    this.chart = chart;
    this.updateChartDataAndStuff();

    //Update the timeline every 5 seconds
    setInterval(() => {
      this.updateChartDataAndStuff();
    }, 5000);
  };
}
